import { v4 as uuidv4 } from 'uuid';
import { get } from 'lodash-es';

import {
    getFilterOptionsForGroup,
    shapeCatalogFilterGroups,
    shapeCatalogSizePreferences,
    shapeSelectedSizesFromString,
    shapeCatalogSortOptions,
} from '~modules/catalog/js/catalog-utils';

import { BROWSE_COLLECTION_PARENT_SLUG } from '~modules/nav/js/nav-constants';
import {
    CATALOG_QUERY_IN_STOCK,
    CATALOG_QUERY_SIZE,
} from '~modules/catalog/js/catalog-constants';

import { NAVIGATION_MODULE_NAME } from '~modules/nav/js/nav-store';
import { PROFILE_MODULE_NAME } from '~modules/profile/js/profile-store';
import { SITE_SETTINGS_MODULE_NAME } from '~modules/site-settings/js/site-settings-store';

export const CATALOG_MODULE_NAME = 'catalog';

/* actions */
export const TOGGLE_IN_BOX_STATUS = 'TOGGLE_IN_BOX_STATUS';

/* mutations */
export const SET_CATALOG_DATA = 'SET_CATALOG_DATA';

export function getCatalogModule(context, options = {
    respectAvailabilityFilterPreference: true,
}) {
    return {
        state: () => ({
            moduleContext: context,
            browseSlug: null,
            products: [],
            filterGroups: [],
            pagination: {},
            sortOptions: [],
            selectedSizes: {
                petite: [],
                plus: [],
                regular: [],
            },
            availabilityFilterIsActive: false,
            responseId: null,
            clientResponseUuid: null,
            respectAvailabilityFilterPreference: options.respectAvailabilityFilterPreference ?? true,
        }),

        /* eslint-disable no-param-reassign */
        mutations: {
            [SET_CATALOG_DATA](state, {
                products,
                pagination,
                filterGroups,
                sortOptions,
                query,
                slug,
                responseId,
                appliedFilters,
            }) {
                state.appliedFilters = appliedFilters;
                state.products = products;
                state.pagination = pagination;
                state.sortOptions = shapeCatalogSortOptions(sortOptions, query, state.moduleContext);
                state.filterGroups = shapeCatalogFilterGroups(filterGroups, query);
                state.browseSlug = slug;
                state.selectedSizes = shapeSelectedSizesFromString(query[CATALOG_QUERY_SIZE]);
                state.availabilityFilterIsActive =
                    query[CATALOG_QUERY_IN_STOCK] === true || query[CATALOG_QUERY_IN_STOCK] === 'true';
                state.responseId = responseId;
                state.clientResponseUuid = uuidv4();
            },
            [TOGGLE_IN_BOX_STATUS](state, choiceIdToToggle) {
                const productIndex = state.products.findIndex(product => product.choiceId === choiceIdToToggle);
                const updatedCatalogItem = state.products[productIndex];
                updatedCatalogItem.inBox = !updatedCatalogItem?.inBox;
                state.products[productIndex] = updatedCatalogItem;
            },
        },

        /* eslint-enable no-param-reassign */
        actions: {},

        getters: {
            filterOptionsForGroup(state) {
                const filterGroups = get(state, 'filterGroups', []);

                return filterGroupName => getFilterOptionsForGroup(filterGroupName, filterGroups);
            },
            totalProductResults(state) {
                return get(state, 'pagination.totalItems', 0);
            },
            /* Choice Ids for the products in the currently viewed catalog page */
            choiceIdsInCatalog(state) {
                const products = get(state, 'products', []);

                return products
                    .map(product => product.choiceId);
            },
            /* List of choiceIds in the catalog that are in the users openBox */
            choiceIdsInCatalogAndBox(state) {
                const products = get(state, 'products', []);

                return products
                    .filter(product => product.inBox)
                    .map(product => product.choiceId);
            },
            /* comma delimited string of selected sizes, to be used in queries */
            selectedSizeList(state) {
                return shapeCatalogSizePreferences(state.selectedSizes || {}).sort().join(', ');
            },
            isCollectionPage(state, getters, rootState) {
                const navItem = get(rootState[NAVIGATION_MODULE_NAME], `flatNavigation.${state.browseSlug}`, {});
                return navItem && navItem.parentSlug === BROWSE_COLLECTION_PARENT_SLUG;
            },
            catalogRequestSettings(state, getters, rootState, rootGetters) {
                const preferredSizeList = rootGetters[`${PROFILE_MODULE_NAME}/preferredSizeList`];
                const applyAvailabilityFilterPreference = get(rootState[SITE_SETTINGS_MODULE_NAME],
                    'preferences.applyAvailabilityFilterPreference',
                    false,
                );
                const catalogFilterWhitelist = rootGetters[`${SITE_SETTINGS_MODULE_NAME}/catalogFilterWhitelist`];
                const catalogFilterExcludePageOneWhiteList = rootGetters[
                    `${SITE_SETTINGS_MODULE_NAME}/catalogFilterExcludePageOneWhiteList`
                ];

                return {
                    preferredSizeList,
                    applyAvailabilityFilterPreference,
                    catalogFilterWhitelist,
                    catalogFilterExcludePageOneWhiteList,
                    respectAvailabilityFilterPreference: state.respectAvailabilityFilterPreference,
                };
            },
        },
    };
}

export default getCatalogModule;
